import React, {
  useRef,
  useCallback,
  ChangeEventHandler,
  useEffect,
  useState,
  ChangeEvent,
  RefObject,
  KeyboardEventHandler,
} from 'react'
import padStart from 'lodash/padStart'
import './DateInput.scss'

export interface DateInputProps {
  // expects ISO format string YYYY-MM-DDTHH:mm:ss(Z)
  // if isDateOnly, it expects YYYY-MM-DD
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  id?: string
  autoFocus?: boolean
  isDateOnly?: boolean
  disabled?: boolean
  placeholder?: Partial<InternalState>
  defaultValues?: Partial<InternalState>
  wrapperClassname?: string
  'data-testid'?: string
}

interface InternalState {
  date: string
  month: string
  year: string
  hour: string
  minute: string
}

const MAXLEN = {
  date: 2,
  month: 2,
  year: 4,
  hour: 2,
  minute: 2,
}

const RANGE = {
  date: [0, 31],
  month: [0, 12],
  year: [0, 9999],
  hour: [0, 23],
  minute: [0, 59],
}

/**
 * A fairly dumb DateTime component with inputs for each date/month/year hour/time element.
 * The onChange() callback is called with whatever the value you entered, no validation is done,
 * nor it try to deal with timezone.
 *
 * However, there are some cool simple things about this component.
 *
 * It will try to prevent user to enter dumb data like:
 * - any number > 31 for date
 * - any number > 12 for year
 * - any number > 23 for hour
 * - any number > 59 for minute
 * - or any text
 *
 * When enough character is filled for one input, it will switch the focus to the next input field automatically.
 *
 * It also handles Backspace press when the input is empty, and move the focus to the previous input.
 */
export const DateInput = React.memo((props: DateInputProps) => {
  const {
    id,
    value,
    autoFocus,
    placeholder,
    isDateOnly,
    disabled,
    onChange,
    onBlur,
    defaultValues,
    wrapperClassname,
  } = props
  const [internalState, setInternalState] = useState<InternalState>(
    convertValueToState(value, Boolean(isDateOnly))
  )

  const valueRef = useRef(value)

  const inputDate = useRef<HTMLInputElement>(null)
  const inputMonth = useRef<HTMLInputElement>(null)
  const inputYear = useRef<HTMLInputElement>(null)
  const inputHour = useRef<HTMLInputElement>(null)
  const inputMinute = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus) {
      moveFocusToElementIf(inputDate, true)
    }
  }, [autoFocus])

  useEffect(() => {
    valueRef.current = value
    setInternalState((currentState) => {
      const computedValue = convertStateToValue(currentState, Boolean(isDateOnly), defaultValues)

      if (value === computedValue) {
        return currentState
      }

      return convertValueToState(value, Boolean(isDateOnly))
    })
  }, [value, isDateOnly, defaultValues])

  const triggerStateUpdate = useCallback(
    (key: keyof InternalState, value: string) => {
      const nextState = { ...internalState, [key]: value }

      setInternalState(nextState)
      const nextValue = convertStateToValue(nextState, Boolean(isDateOnly), defaultValues)
      onChange(nextValue)
    },
    [internalState, onChange, isDateOnly, defaultValues]
  )

  const handleKeyDown: (type: keyof InternalState) => KeyboardEventHandler = useCallback(
    (type) => (e) => {
      // if user press Backspace and the value is empty
      // move focus to previous input
      if (e.keyCode === 8) {
        const { minute, hour, year, month } = internalState
        const movements = [
          moveFocusToElementIf(inputHour, type === 'minute' && !minute),
          moveFocusToElementIf(inputYear, type === 'hour' && !hour),
          moveFocusToElementIf(inputMonth, type === 'year' && !year),
          moveFocusToElementIf(inputDate, type === 'month' && !month),
        ]
        if (movements.some((mov) => Boolean(mov))) {
          // need to prevent default otherwise the the
          // next input in focus will have existing text deleted
          e.preventDefault()
        }
      }
    },
    [internalState]
  )

  const handleChange: (
    type: keyof InternalState
  ) => ChangeEventHandler<HTMLInputElement> = useCallback(
    (type) => (e) => {
      try {
        const maxLen = MAXLEN[type]
        const range = RANGE[type] as [number, number]
        const val = getValidValue(e, maxLen, range)
        moveFocusToElementIf(inputMonth, type === 'date' && val.length === 2)
        moveFocusToElementIf(inputYear, type === 'month' && val.length === 2)
        moveFocusToElementIf(inputHour, type === 'year' && val.length === 4)
        moveFocusToElementIf(inputMinute, type === 'hour' && val.length === 2)
        triggerStateUpdate(type, val)
      } catch (e) {
        // silent
      }
    },
    [triggerStateUpdate]
  )

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur()
    }
  }, [onBlur])
  const effectivePlaceholder = isStateEmpty(internalState) ? placeholder : null

  return (
    <div
      className={`date-input-wrapper${wrapperClassname ? ' ' + wrapperClassname : ''}`}
      id={id}
      data-testid={props['data-testid']}
    >
      <input
        className="date-input-date form-control"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder={effectivePlaceholder?.date ?? 'DD'}
        aria-label="Day"
        ref={inputDate}
        value={internalState.date}
        onChange={handleChange('date')}
        onKeyDown={handleKeyDown('date')}
        onBlur={handleBlur}
        disabled={disabled}
      />
      <input
        className="date-input-month form-control"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        ref={inputMonth}
        placeholder={effectivePlaceholder?.month ?? 'MM'}
        aria-label="Month"
        value={internalState.month}
        onChange={handleChange('month')}
        onKeyDown={handleKeyDown('month')}
        onBlur={handleBlur}
        disabled={disabled}
      />
      <input
        className="date-input-year form-control"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        ref={inputYear}
        placeholder={effectivePlaceholder?.year ?? 'YYYY'}
        aria-label="Year"
        value={internalState.year}
        onChange={handleChange('year')}
        onKeyDown={handleKeyDown('year')}
        onBlur={handleBlur}
        disabled={disabled}
      />
      {isDateOnly ? null : (
        <>
          <input
            className="DateInput-hour form-control"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            ref={inputHour}
            placeholder={effectivePlaceholder?.hour ?? 'HH'}
            value={internalState.hour}
            onChange={handleChange('hour')}
            onKeyDown={handleKeyDown('hour')}
            onBlur={handleBlur}
            disabled={disabled}
          />
          <span className="Hour-colon">:</span>
          <input
            className="DateInput-minute form-control"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            ref={inputMinute}
            placeholder={effectivePlaceholder?.minute ?? 'mm'}
            value={internalState.minute}
            disabled={disabled}
            onChange={handleChange('minute')}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown('minute')}
          />
        </>
      )}
    </div>
  )
})

export function convertValueToState(val: string, isDateOnly: boolean): InternalState {
  if (val) {
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z)?$/
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    const isValid = isDateOnly ? dateRegex.test(val) : dateTimeRegex.test(val)

    if (isValid) {
      const matches = val.match(/\d+/g)

      if (matches && matches.length) {
        const [year, month, date, hour, minute] = matches
        return {
          year: year || '',
          month: month || '',
          date: date || '',
          hour: hour || '',
          minute: minute || '',
        }
      }
    }
  }
  return emptyState()
}

export function convertStateToValue(
  state: InternalState,
  isDateOnly: boolean,
  defaultValues?: Partial<InternalState>
): string {
  const { date, month, year, hour, minute } = state

  if (date || month || year || hour || minute) {
    const dateString = `${pad4(year || defaultValues?.year || '0000')}-${pad2(
      month || defaultValues?.month || '01'
    )}-${pad2(date || defaultValues?.date || '01')}`
    const hourString = `${pad2(hour || defaultValues?.hour || '00')}:${pad2(
      minute || defaultValues?.minute || '00'
    )}:00`

    return isDateOnly ? dateString : `${dateString}T${hourString}`
  }

  return ''
}

function emptyState(): InternalState {
  return {
    date: '',
    month: '',
    year: '',
    hour: '',
    minute: '',
  }
}

const pad2 = (str: string | number) => padStart(String(str), 2, '0')
const pad4 = (str: string | number) => padStart(String(str), 4, '0')

function getValidValue(e: ChangeEvent<HTMLInputElement>, maxLen: number, range?: [number, number]) {
  // make sure that user do not enter text
  if (!e.target.validity.valid) {
    throw new Error()
  }
  const rawValue = e.target.value

  if (rawValue.length > maxLen) {
    throw new Error()
  }

  const value = parseInt(e.target.value)

  if (range) {
    if (value < range[0] || value > range[1]) {
      throw new Error()
    }
  }

  return rawValue
}

function moveFocusToElementIf(el: RefObject<null | HTMLInputElement>, condition: boolean) {
  if (condition) {
    el.current?.focus()
    el.current?.select()
    return true
  }
  return false
}

function isStateEmpty(state: InternalState) {
  return Object.entries(state).every((_key, value) => !value)
}
